import { useEffect, useRef, useState } from 'react';

import { useMenuItemFinderLang } from './useMenuItemFinderLang';
import { useGetItemsReplacement } from './useGetItemsReplacement';
import { useGetSuggestionsWithAI } from './useGetSuggestionsWithAI';

import { NAMES_OF_SEARCH_FIELDS } from '../menuItemFinder.const';

import type {
  SelectedItemsOutStock,
  SelectSuggestionsParams,
  ProductReplacement,
  ToppingReplacement,
} from 'features/order/substitution-flow/domain';
import type { OrderId } from 'shared/domain';
import type { ChangeEvent, MouseEvent, FocusEvent } from 'react';

export function useMenuItemFinder(props: Props) {
  const { orderId, itemsOutStock, onSelectSuggestions } = props;

  const basicProps = {
    orderId,
    isProduct: itemsOutStock.products.length > 0,
    productId:
      itemsOutStock.products[0]?.id ?? itemsOutStock.toppings[0].productId,
    toppingId: itemsOutStock.toppings[0]?.detail.id,
  };
  const itemTitle = basicProps.isProduct
    ? itemsOutStock.products[0].name
    : itemsOutStock.toppings[0].detail.description;

  const refTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputFirstRef = useRef<HTMLInputElement | null>(null);
  const inputSecondRef = useRef<HTMLInputElement | null>(null);

  const [queryInput1, setQueryInput1] = useState('');
  const [queryInput2, setQueryInput2] = useState('');
  const [isInput1Focused, setIsInput1Focused] = useState(false);
  const [isInput2Focused, setIsInput2Focused] = useState(false);
  const [isSelection1Done, setIsSelection1Done] = useState(false);
  const [isSelection2Done, setIsSelection2Done] = useState(false);

  const {
    data: suggestionWithAI,
    isFetching: isFetchingSuggestionWithAI,
  } = useGetSuggestionsWithAI({ ...basicProps });
  const { data: respOfQuery1 } = useGetItemsReplacement({
    ...basicProps,
    query: queryInput1,
  });
  const { data: respOfQuery2 } = useGetItemsReplacement({
    ...basicProps,
    query: queryInput2,
  });

  const lang = useMenuItemFinderLang();

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newValue = value.trim();
    const isFieldNameTheFirst = name === NAMES_OF_SEARCH_FIELDS.first;
    clearTimeout(refTimeout.current as NodeJS.Timeout);
    if (isFieldNameTheFirst) setIsSelection1Done(false);
    else setIsSelection2Done(false);
    onSelectSuggestions({
      index: isFieldNameTheFirst ? 0 : 1,
      isProduct: basicProps.isProduct,
      isFromAI: false,
    });
    refTimeout.current = setTimeout(() => {
      if (isFieldNameTheFirst) setQueryInput1(newValue);
      if (!isFieldNameTheFirst) setQueryInput2(newValue);
    }, 500);
  };

  const handleClearTextField = (event: MouseEvent<HTMLButtonElement>) => {
    const $input = event.currentTarget
      .closest('.MuiInputBase-root')
      ?.querySelector('input');
    if ($input) {
      $input.value = '';
      handleOnChange({
        target: {
          name: $input.name,
          value: $input.value,
        },
      } as ChangeEvent<HTMLInputElement>);
    }
  };

  const handleSelectSuggestion = (event: MouseEvent<HTMLElement>) => {
    const $item = event.currentTarget;
    const $targetInput = $item
      .closest('[aria-label="field-container"]')
      ?.querySelector('input') as HTMLInputElement;
    const itemId = parseInt($item.dataset.itemId ?? '0');
    const fieldName = $targetInput.name;
    const isFieldNameTheFirst = fieldName === NAMES_OF_SEARCH_FIELDS.first;
    const results = isFieldNameTheFirst ? respOfQuery1 : respOfQuery2;
    const product = results.products.filter(p => p.id === itemId)[0];
    const topping = results.toppings.filter(t => t.id === itemId)[0];
    const newValue = basicProps.isProduct ? product.name : topping.name;
    if (fieldName && newValue) {
      $targetInput.value = newValue;
      handleOnChange({
        target: {
          name: fieldName,
          value: newValue,
        },
      } as ChangeEvent<HTMLInputElement>);
      const index = isFieldNameTheFirst ? 0 : 1;
      if (isFieldNameTheFirst) setIsSelection1Done(true);
      else setIsSelection2Done(true);
      onSelectSuggestions(
        basicProps.isProduct
          ? { isProduct: true, product, index, isFromAI: false }
          : { isProduct: false, topping, index, isFromAI: false },
      );
    }
  };

  const selectSuggestionWithAI = () => {
    const _list = basicProps.isProduct
      ? suggestionWithAI.products
      : suggestionWithAI.toppings;
    for (let idx = 0; idx < _list.length; idx++) {
      const suggestion = _list[idx];
      if (idx === 0) {
        inputFirstRef.current!.value = suggestion.name;
        setIsSelection1Done(true);
      } else if (idx === 1) {
        inputSecondRef.current!.value = suggestion.name;
        setIsSelection2Done(true);
      }
      onSelectSuggestions(
        basicProps.isProduct
          ? {
              isProduct: true,
              product: suggestion as ProductReplacement,
              index: idx,
              isFromAI: true,
            }
          : {
              isProduct: false,
              topping: suggestion as ToppingReplacement,
              index: idx,
              isFromAI: true,
            },
      );
    }
  };

  const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    if (name === NAMES_OF_SEARCH_FIELDS.first) setIsInput1Focused(true);
    if (name === NAMES_OF_SEARCH_FIELDS.second) setIsInput2Focused(true);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setTimeout(() => {
      if (name === NAMES_OF_SEARCH_FIELDS.first) setIsInput1Focused(false);
      if (name === NAMES_OF_SEARCH_FIELDS.second) setIsInput2Focused(false);
    }, 250);
  };

  useEffect(() => {
    return () => {
      const _INIT = { isProduct: basicProps.isProduct, isFromAI: false };
      onSelectSuggestions({ index: 0, ..._INIT });
      onSelectSuggestions({ index: 1, ..._INIT });
    };
  }, []);

  useEffect(() => {
    const hasSuggestionWithAI =
      suggestionWithAI.products.length > 0 ||
      suggestionWithAI.toppings.length > 0;
    if (hasSuggestionWithAI) selectSuggestionWithAI();
  }, [suggestionWithAI]);

  return {
    lang,
    itemTitle,
    isFetchingSuggestionWithAI,
    isProduct: basicProps.isProduct,
    input1: {
      ref: inputFirstRef,
      query: queryInput1,
      results: respOfQuery1,
      isFocused: isInput1Focused,
      isDone: isSelection1Done,
    },
    input2: {
      ref: inputSecondRef,
      query: queryInput2,
      results: respOfQuery2,
      isFocused: isInput2Focused,
      isDone: isSelection2Done,
    },
    handleOnChange,
    handleOnFocus,
    handleOnBlur,
    handleClearTextField,
    handleSelectSuggestion,
  };
}

interface Props {
  orderId: OrderId;
  itemsOutStock: SelectedItemsOutStock;
  onSelectSuggestions(params: SelectSuggestionsParams): void;
}
